'use client';

import { c as _c } from "react/compiler-runtime";
import type { PrV2CommentResponse, PrV2DiscussionResponse } from '@/lib/pr-v2/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchPrV2Comments } from './fetch-pr-v2';
import { reconcilePrV2CommentsWithOptimistic } from './optimistic-comment-reconciliation';
import { PR_V2_STANDARD_STALE_TIME_MS } from './query-config';
import { prV2CommentsQueryKey, prV2DiscussionQueryKey } from './query-keys';
import { pruneStableViewKeys } from '@/lib/pr-v2/stable-view-keys';
export function usePrV2Comments(owner, repo, number, options) {
  const $ = _c(20);
  const queryClient = useQueryClient();
  const scopeKey = `${owner}/${repo}/${number}`;
  let t0;
  if ($[0] !== number || $[1] !== owner || $[2] !== repo) {
    t0 = prV2CommentsQueryKey(owner, repo, number);
    $[0] = number;
    $[1] = owner;
    $[2] = repo;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const queryKey = t0;
  let t1;
  if ($[4] !== number || $[5] !== owner || $[6] !== repo) {
    t1 = prV2DiscussionQueryKey(owner, repo, number);
    $[4] = number;
    $[5] = owner;
    $[6] = repo;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const discussionKey = t1;
  let t2;
  if ($[8] !== discussionKey || $[9] !== number || $[10] !== owner || $[11] !== queryClient || $[12] !== queryKey || $[13] !== repo || $[14] !== scopeKey) {
    t2 = async t3 => {
      const {
        signal
      } = t3;
      const fetchedComments = await fetchPrV2Comments(owner, repo, number, signal);
      const existingComments = queryClient.getQueryData(queryKey);
      const result = reconcilePrV2CommentsWithOptimistic(existingComments, fetchedComments);
      const currentDiscussion = queryClient.getQueryData(discussionKey);
      const activeStableViewKeyIds = new Set();
      for (const comment of result) {
        activeStableViewKeyIds.add(comment.id);
        activeStableViewKeyIds.add(comment.githubCommentId);
      }
      for (const item of currentDiscussion?.timelineItems ?? []) {
        if (typeof item.id === "string" && item.id.length > 0) {
          activeStableViewKeyIds.add(item.id);
        }
      }
      pruneStableViewKeys(scopeKey, activeStableViewKeyIds);
      return result;
    };
    $[8] = discussionKey;
    $[9] = number;
    $[10] = owner;
    $[11] = queryClient;
    $[12] = queryKey;
    $[13] = repo;
    $[14] = scopeKey;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  const t3 = Boolean(owner && repo && number) && (options?.enabled ?? true);
  let t4;
  if ($[16] !== queryKey || $[17] !== t2 || $[18] !== t3) {
    t4 = {
      queryKey,
      queryFn: t2,
      enabled: t3,
      placeholderData: _temp,
      staleTime: PR_V2_STANDARD_STALE_TIME_MS,
      refetchOnWindowFocus: false
    };
    $[16] = queryKey;
    $[17] = t2;
    $[18] = t3;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return useQuery(t4);
}
function _temp() {}